import React, {useEffect} from "react";
import {Box, Typography, Button} from "@mui/material";
import {images} from "../../assets";
import {useNavigate, useLocation} from "react-router-dom";
import { logger } from "../../store/middleware/consoleEventLogger";
import {getSessionStatus, cancelVerification} from "../../services/faceLiveness";
import {useLanguage} from "../../contexts/LanguageContext";
import useVerifiedNavigation from "../../hooks/useVerifiedNavigation";

const LoadingSpinner = () => (
  <Box
    sx={{
      position: 'relative',
      flexShrink: 0,
      width: '104px',
      height: '104px',
      animation: 'spin 1s linear infinite',
      '@keyframes spin': {
        '0%': {transform: 'rotate(0deg)'},
        '100%': {transform: 'rotate(360deg)'},
      },
    }}
  >
    <svg style={{display: 'block', width: '100%', height: '100%'}} fill="none" preserveAspectRatio="none"
         viewBox="0 0 104 104">
      <g>
        <path
          d="M52 8.66667C43.4295 8.66667 35.0514 11.2081 27.9253 15.9697C20.7992 20.7312 15.245 27.4989 11.9652 35.4171C8.68542 43.3352 7.82728 52.0481 9.49931 60.4539C11.1713 68.8598 15.2984 76.581 21.3587 82.6413C27.419 88.7016 35.1402 92.8287 43.5461 94.5007C51.9519 96.1727 60.6648 95.3146 68.583 92.0348C76.5011 88.755 83.2688 83.2008 88.0303 76.0747C92.7919 68.9486 95.3333 60.5705 95.3333 52C95.3333 46.3094 94.2125 40.6745 92.0348 35.4171C89.8571 30.1596 86.6652 25.3826 82.6413 21.3587C78.6174 17.3348 73.8404 14.1429 68.583 11.9652C63.3255 9.78752 57.6906 8.66667 52 8.66667ZM52 86.6667C45.1436 86.6667 38.4411 84.6335 32.7402 80.8243C27.0393 77.0151 22.596 71.6009 19.9722 65.2664C17.3483 58.9319 16.6618 51.9615 17.9994 45.2369C19.3371 38.5122 22.6387 32.3352 27.487 27.487C32.3352 22.6387 38.5122 19.3371 45.2369 17.9994C51.9615 16.6618 58.9319 17.3483 65.2664 19.9722C71.6009 22.596 77.0151 27.0393 80.8243 32.7402C84.6335 38.4411 86.6667 45.1436 86.6667 52C86.6667 61.1942 83.0143 70.0118 76.513 76.513C70.0118 83.0143 61.1942 86.6667 52 86.6667Z"
          fill="#004ADD" opacity="0.5"/>
        <path
          d="M86.6667 52H95.3333C95.3333 46.3094 94.2125 40.6745 92.0348 35.417C89.8571 30.1596 86.6652 25.3826 82.6413 21.3587C78.6174 17.3348 73.8404 14.1429 68.5829 11.9652C63.3255 9.78752 57.6906 8.66667 52 8.66667V17.3333C61.1942 17.3333 70.0118 20.9857 76.513 27.487C83.0143 33.9882 86.6667 42.8058 86.6667 52Z"
          fill="#004ADD"/>
      </g>
    </svg>
  </Box>
);

export default function LoadingQrCode() {
  const navigate = useNavigate();
  const location = useLocation();
  const {t} = useLanguage();

  const sessionId = location.state?.sessionId;
  const language = location.state?.language || "en";
  const redirectUrl = location.state?.redirectUrl;
  const polingInterval = 3000;

  const handleVerifiedNavigation = useVerifiedNavigation();

  useEffect(() => {
    if (!sessionId) return;
    const interval = setInterval(async () => {
      try {
        const response = await getSessionStatus(sessionId);
        const status = response?.data?.status;
        const success_page_display = response?.data?.success_page_display;
        const currentRedirectUrl = response?.data?.redirect_url || redirectUrl;
        if (status === 'success') {
          clearInterval(interval);
          handleVerifiedNavigation({language, url: currentRedirectUrl}, success_page_display);
        } else if (status === 'fail' || status === 'error' || status === 'timeout') {
          clearInterval(interval);
          navigate("/error", {state: {language, redirect_url: currentRedirectUrl, reason: 'unverified'}});
        }
      } catch (err) {
        logger.error("Polling error in Loading screen:", err);
      }
    }, polingInterval);

    return () => clearInterval(interval);
  }, [sessionId, navigate, language, redirectUrl]);

  const handleCancel = async () => {
    try {
      if (sessionId) {
        await cancelVerification(sessionId);
      }
      navigate(`/verification/${sessionId}`);
    } catch (error) {
      logger.error("Failed to cancel verification:", error);
      // Fallback behavior if API call fails
      navigate("/failed", {state: {redirect_url: redirectUrl}});
    }
  };

  return (
    <Box sx={{
      bgcolor: '#f5f5f7',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      pb: '197px',
      pt: '20px',
      minHeight: '100vh'
    }}>
      <Box sx={{display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center', width: '100%'}}>
        <Box display="flex" justifyContent="center" sx={{pt: 1, mb: 10}}>
          <img
            src={images.certageLogo}
            alt="Logo"
            style={{
              width: "min(70vw, 300px)",
              height: "auto",
              maxHeight: "140px",
            }}
          />
        </Box>

        <Box sx={{display: 'flex', flexDirection: 'column', gap: '21px', alignItems: 'center', width: '100%'}}>
          <LoadingSpinner/>

          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '38px',
            alignItems: 'center',
            color: '#403e3e',
            textAlign: 'center',
            maxWidth: '729px',
            px: 2
          }}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              alignItems: 'center',
              lineHeight: 1.375,
              fontSize: '20px',
              width: '100%'
            }}>
              <Typography sx={{fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: '20px', width: '100%'}}>
                {t.loadingMobile.title}
              </Typography>
              <Typography sx={{fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '20px', width: '100%'}}>
                {t.loadingMobile.subtitle}
              </Typography>
            </Box>

            <Typography sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              lineHeight: 1.375,
              fontSize: '13px',
              width: '100%'
            }}>
              <span>{t.loadingMobile.dontWantToContinue}</span>
              <Button
                onClick={handleCancel}
                sx={{
                  textTransform: 'none',
                  p: 0,
                  minWidth: 0,
                  color: '#387fc2',
                  fontFamily: 'inherit',
                  fontWeight: 'inherit',
                  fontSize: 'inherit',
                  '&:hover': {textDecoration: 'underline', bgcolor: 'transparent'}
                }}
              >
                {t.loadingMobile.returnToWebsite}
              </Button>
              <span>.</span>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
